import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { DisclaimerProvider } from "@/context/DisclaimerContext";
import { FeedbackProvider } from "@/context/FeedbackContext";
import { SyncProvider } from "@/context/SyncContext";
import { TierProvider } from "@/context/TierContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PersistenceRecovery } from "@/components/PersistenceRecovery";
import { LogoutGuardDialog } from "@/components/LogoutGuardDialog";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Start from "@/pages/Start";
import QuickStart from "@/pages/QuickStart";
import Welcome from "@/pages/Welcome";
import Capture from "@/pages/Capture";
import PropertyDetail from "@/pages/PropertyDetail";
import Compare from "@/pages/Compare";
import EvidenceCoverage from "@/pages/EvidenceCoverage";
import SelfAudit from "@/pages/SelfAudit";
import MoveOutDay from "@/pages/MoveOutDay";
import MeterSerialVault from "@/pages/MeterSerialVault";
import DemoMode from "@/pages/DemoMode";
import KeyReturnCapsule from "@/pages/KeyReturnCapsule";
import ConditionHistory from "@/pages/ConditionHistory";
import TenancyTimeline from "@/pages/TenancyTimeline";
import DisputeLetter from "@/pages/DisputeLetter";
import Legal from "@/pages/Legal";
import ReturnRequest from "@/pages/ReturnRequest";
import SmallClaims from "@/pages/SmallClaims";
import PublicDisputeLetter from "@/pages/PublicDisputeLetter";
import Settings from "@/pages/Settings";
import PublicReport from "@/pages/PublicReport";
import PublicSlugReport from "@/pages/PublicSlugReport";
import Verify from "@/pages/Verify";
import PublicSurrender from "@/pages/PublicSurrender";
import PublicAttest from "@/pages/PublicAttest";
import PublicWitness from "@/pages/PublicWitness";
import PublicMaintenanceShare from "@/pages/PublicMaintenanceShare";
import LandlordBaselineView from "@/pages/LandlordBaselineView";
import AdminFeedback from "@/pages/AdminFeedback";
import HelpCenter from "@/pages/HelpCenter";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCanceled from "@/pages/BillingCanceled";
import { FeedbackWidget } from "@/components/FeedbackWidget";

function AppRouter() {
  const location = useLocation();
  // Dark theme applies to the authenticated app shell only; public/marketing pages stay light.
  useEffect(() => {
    const DARK_PREFIXES = ["/dashboard", "/welcome", "/start", "/property", "/properties", "/compare", "/settings", "/admin", "/capture"];
    const isDark = DARK_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    document.documentElement.classList.toggle("theme-dark", isDark);
  }, [location.pathname]);
  // Process OAuth callback FIRST (synchronously during render) to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/terms" element={<Legal kind="terms" />} />
      <Route path="/ai-limitations" element={<Legal kind="ai-limitations" />} />
      <Route path="/data-deletion" element={<Legal kind="data-deletion" />} />
      <Route path="/acceptable-use" element={<Legal kind="acceptable-use" />} />
      <Route path="/report/view/:token" element={<PublicReport />} />
      <Route path="/report/:slug" element={<PublicSlugReport />} />
      <Route path="/verify/:fingerprint" element={<Verify />} />
      <Route path="/surrender/:token" element={<PublicSurrender />} />
      <Route path="/attest/:token" element={<PublicAttest />} />
      <Route path="/witness/:slug" element={<PublicWitness />} />
      <Route path="/maintenance/:slug" element={<PublicMaintenanceShare />} />
      <Route path="/baseline/:slug" element={<LandlordBaselineView />} />
      <Route path="/dispute/view/:token" element={<PublicDisputeLetter />} />
      <Route path="/properties/:id/dispute-letter" element={<ProtectedRoute><DisputeLetter /></ProtectedRoute>} />
      <Route path="/properties/:id/return-request" element={<ProtectedRoute><ReturnRequest /></ProtectedRoute>} />
      <Route path="/properties/:id/small-claims" element={<ProtectedRoute><SmallClaims /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
      <Route path="/start" element={<ProtectedRoute><Start /></ProtectedRoute>} />
      <Route path="/quick-start" element={<ProtectedRoute><QuickStart /></ProtectedRoute>} />
      <Route path="/capture/:propertyId/:roomId" element={<ProtectedRoute><Capture /></ProtectedRoute>} />
      <Route path="/property/:propertyId" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
      <Route path="/compare/:propertyId" element={<ProtectedRoute><Compare /></ProtectedRoute>} />
      <Route path="/coverage/:propertyId" element={<ProtectedRoute><EvidenceCoverage /></ProtectedRoute>} />
      <Route path="/self-audit/:propertyId" element={<ProtectedRoute><SelfAudit /></ProtectedRoute>} />
      <Route path="/move-out-day/:propertyId" element={<ProtectedRoute><MoveOutDay /></ProtectedRoute>} />
      <Route path="/vault/:propertyId" element={<ProtectedRoute><MeterSerialVault /></ProtectedRoute>} />
      <Route path="/demo" element={<DemoMode />} />
      <Route path="/public/demo" element={<Navigate to="/demo" replace />} />
      <Route path="/key-return-capsule/:propertyId" element={<ProtectedRoute><KeyReturnCapsule /></ProtectedRoute>} />
      <Route path="/condition-history/:propertyId" element={<ProtectedRoute><ConditionHistory /></ProtectedRoute>} />
      <Route path="/tenancy-timeline/:propertyId" element={<ProtectedRoute><TenancyTimeline /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route path="/billing/canceled" element={<ProtectedRoute><BillingCanceled /></ProtectedRoute>} />
      <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedback /></ProtectedRoute>} />
      </Routes>
      <FeedbackWidget />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DisclaimerProvider>
            <FeedbackProvider>
              <SyncProvider>
                <TierProvider>
                  <AppRouter />
                  <PersistenceRecovery />
                  <LogoutGuardDialog />
                  <Toaster position="top-center" richColors />
                </TierProvider>
              </SyncProvider>
            </FeedbackProvider>
          </DisclaimerProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
